// path: src/Recap.js

import React, { useEffect, useState } from "react";
import "./App.css";
import AppFooter from "./Components/AppFooter";
import {
    getStoryMainLevelCount,
    getTotalStoryLevels,
    isUnderworldLevel,
} from "./data/storyLevels";
import {
    HEART_REFILL_MS,
    MAX_HEARTS,
    formatHeartRefillCountdown,
    formatHeartsMeter,
    getHeartRefillMsRemaining,
} from "./utils/heartEconomy";
import { getRecapScoreCallout } from "./utils/recapCopy";
import {
    getRewardDoubleButtonLabel,
    getVisibleEarnedRewardOffers,
} from "./utils/rewardedEarnedRewards";

const Recap = ({
    outcome = "loss", // "win" | "loss"
    levelReached = 1,
    correctClicks = 0,
    foilsFound = 0,
    hiddenBoostsFound = 0,
    hiddenPacksFound = 0,
    newlyUnlocked = 0,
    treatsEarned = 0,
    boostBundlesEarned = 0,
    treatBalance = 0,
    totalUnlocked = 0,
    totalCards = 113,
    runMode = "normal",
    runType = "endless",
    storyLevelId = null,
    nextStoryLevelId = null,
    runEndReason = "",
    runElapsedText = "0:00",
    unlockMessage = "",
    score = 0,
    bestScore = 0,
    showScore = false,
    showBest = false,
    hearts = MAX_HEARTS,
    heartLastRefillAt = Date.now(),
    maxHearts = MAX_HEARTS,
    hardcoreMode = false,
    heartRewardBusy = "",
    heartRewardError = "",
    adStatus = null,
    adsRemoved = false,
    onWatchAd,
    earnedRewardOffers = [],
    doubledEarnedRewardIds = [],
    rewardDoubleAdStatus = null,
    earnedRewardDoubleBusy = "",
    earnedRewardDoubleError = "",
    onWatchAdForDouble,
    onBuyRefill,
    onPlayAgain,
    onMainMenu,
    onStoryMode,
    onEndlessMode,
    onContinueStory,
    onPrivacyPolicy,
    onTermsOfService,
    banner = null,
}) => {
    const isStory = runType === "story";
    const isBlitz = runMode === "blitz";
    const isLoss = outcome !== "win";
    // Number(null) === 0, so require a real positive id (finale passes null).
    const parsedNextStoryLevelId = Number(nextStoryLevelId);
    const hasNextStoryStage =
        isStory &&
        outcome === "win" &&
        nextStoryLevelId != null &&
        nextStoryLevelId !== "" &&
        Number.isFinite(parsedNextStoryLevelId) &&
        parsedNextStoryLevelId >= 1 &&
        typeof onContinueStory === "function";
    const mainStoryLevelCount = getStoryMainLevelCount();
    const clearedMainStoryFinale =
        isStory &&
        outcome === "win" &&
        Number(storyLevelId) === mainStoryLevelCount;
    const clearedUnderworldFinale =
        isStory &&
        outcome === "win" &&
        Number(storyLevelId) === getTotalStoryLevels();
    const enteringUnderworld =
        clearedMainStoryFinale &&
        hasNextStoryStage &&
        isUnderworldLevel(parsedNextStoryLevelId);

    const title = isStory
        ? outcome === "win"
            ? "Story Level Cleared!"
            : "Story Attempt Ended"
        : outcome === "win"
          ? "You Won!"
          : "Run Ended";

    const subtitle = isStory
        ? outcome === "win"
            ? `Story Level ${storyLevelId || levelReached} complete.`
            : runEndReason === "blitz-timeout"
              ? `Time ran out on Story Level ${storyLevelId || levelReached}.`
              : `Story Level ${storyLevelId || levelReached} was not completed.`
        : isBlitz && runEndReason === "blitz-timeout"
          ? `Time ran out at Level ${levelReached}.`
          : outcome === "win"
            ? "Great Endless run."
            : `You reached Level ${levelReached}.`;

    const scoreCallout =
        showScore && showBest && !isStory
            ? getRecapScoreCallout(score, bestScore)
            : null;
    const safeHearts = Math.max(0, Math.floor(Number(hearts) || 0));
    const outOfHearts = safeHearts <= 0;
    const showHeartOffer =
        isLoss && outOfHearts && typeof onBuyRefill === "function";
    const showWatchAd =
        showHeartOffer && !adsRemoved && typeof onWatchAd === "function";
    const adAvailable = Boolean(adStatus?.available);
    const adRemaining =
        adStatus && Number.isFinite(Number(adStatus.remainingToday))
            ? Number(adStatus.remainingToday)
            : null;
    const isBusy = Boolean(heartRewardBusy || earnedRewardDoubleBusy);
    const rewardDoubleAdAvailable = Boolean(rewardDoubleAdStatus?.available);
    const rewardDoubleAdRemaining =
        rewardDoubleAdStatus &&
        Number.isFinite(Number(rewardDoubleAdStatus.remainingToday))
            ? Number(rewardDoubleAdStatus.remainingToday)
            : null;
    const visibleRewardDoubles = getVisibleEarnedRewardOffers(
        earnedRewardOffers,
        {
            doubledOfferIds: doubledEarnedRewardIds,
            adsRemoved,
            adStatus: rewardDoubleAdStatus,
        }
    );
    const showEarnedRewardDoubles =
        outcome === "win" &&
        visibleRewardDoubles.length > 0 &&
        !adsRemoved &&
        typeof onWatchAdForDouble === "function";

    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        if (!isLoss || safeHearts >= maxHearts) {
            return undefined;
        }
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, [isLoss, safeHearts, maxHearts]);

    const refillCountdown = formatHeartRefillCountdown(
        getHeartRefillMsRemaining(
            safeHearts,
            heartLastRefillAt,
            now,
            maxHearts,
            HEART_REFILL_MS
        )
    );

    const handlePlayAgain = () => {
        if (isStory && typeof onStoryMode === "function") {
            onStoryMode();
            return;
        }

        if (typeof onEndlessMode === "function") {
            onEndlessMode();
            return;
        }

        if (typeof onPlayAgain === "function") {
            onPlayAgain();
        }
    };

    const handleRetryStory = () => {
        if (typeof onPlayAgain === "function" && storyLevelId) {
            onPlayAgain({ storyLevelId });
            return;
        }

        if (typeof onStoryMode === "function") {
            onStoryMode();
        }
    };

    const retryLabel = isStory ? "Try Again" : "Play Again";
    const retryHandler = isStory ? handleRetryStory : handlePlayAgain;

    return (
        <div className="recapScreen">
            {banner}
            <div className="recapCard">
                <h1 className="recapTitle">{title}</h1>

                <div className="recapSubTitle">{subtitle}</div>

                {unlockMessage ? (
                    <div
                        className="recapUnlockMessage"
                        role="status"
                        aria-live="polite"
                    >
                        {unlockMessage}
                    </div>
                ) : null}

                {isStory && Number(treatsEarned) > 0 ? (
                    <div className="recapTreats" role="status">
                        +{Number(treatsEarned).toLocaleString("en-US")} Treats
                        {(doubledEarnedRewardIds || []).includes("treats") ? (
                            <span className="recapTreats__doubled"> · Doubled</span>
                        ) : null}
                    </div>
                ) : null}

                {isStory && Number(boostBundlesEarned) > 0 ? (
                    <div className="recapTreats" role="status">
                        +
                        {Number(boostBundlesEarned) === 1
                            ? "Boost Bundle"
                            : `${Number(boostBundlesEarned)} Boost Bundles`}{" "}
                        · Reveal, Shield &amp; Freeze added to Inventory
                    </div>
                ) : null}

                {showEarnedRewardDoubles ? (
                    <div className="recapEarnedRewards">
                        <div className="recapEarnedRewards__title">
                            Double your rewards
                        </div>
                        <p className="recapEarnedRewards__message">
                            Watch a short ad to earn the same reward again.
                        </p>

                        {earnedRewardDoubleError ? (
                            <p
                                className="recapEarnedRewards__error"
                                role="alert"
                            >
                                {earnedRewardDoubleError}
                            </p>
                        ) : null}

                        <div className="recapEarnedRewards__actions">
                            {visibleRewardDoubles.map((offer) => {
                                const offerBusy =
                                    earnedRewardDoubleBusy === offer.id;

                                return (
                                    <div
                                        key={offer.id}
                                        className="recapEarnedRewards__row"
                                    >
                                        <div className="recapEarnedRewards__label">
                                            {offer.label}
                                        </div>
                                        <button
                                            type="button"
                                            className="homePrimaryButton"
                                            onClick={() =>
                                                onWatchAdForDouble(offer.id)
                                            }
                                            disabled={
                                                isBusy || !rewardDoubleAdAvailable
                                            }
                                        >
                                            {offerBusy
                                                ? "Watching…"
                                                : rewardDoubleAdAvailable
                                                  ? getRewardDoubleButtonLabel(
                                                        offer
                                                    )
                                                  : "Ad limit reached"}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>

                        {rewardDoubleAdRemaining != null &&
                        rewardDoubleAdAvailable ? (
                            <p className="recapEarnedRewards__hint">
                                {rewardDoubleAdRemaining} double-ad
                                {rewardDoubleAdRemaining === 1 ? "" : "s"} left
                                today
                                {rewardDoubleAdStatus?.isPreview
                                    ? " · preview"
                                    : ""}
                            </p>
                        ) : null}
                    </div>
                ) : null}

                {showScore && !isStory && (
                    <div
                        className={`recapScoreBlock${
                            scoreCallout?.kind === "personal-best"
                                ? " recapScoreBlock--best"
                                : scoreCallout?.kind === "near-miss"
                                  ? " recapScoreBlock--near"
                                  : ""
                        }`}
                    >
                        <div className="recapScoreBlock__row">
                            <span className="recapScoreBlock__label">Score</span>
                            <span className="recapScoreBlock__value">{score}</span>
                        </div>
                        {showBest ? (
                            <div className="recapScoreBlock__row">
                                <span className="recapScoreBlock__label">Best</span>
                                <span className="recapScoreBlock__value">
                                    {bestScore}
                                </span>
                            </div>
                        ) : null}
                        {scoreCallout ? (
                            <div
                                className="recapScoreBlock__callout"
                                role="status"
                            >
                                {scoreCallout.text}
                            </div>
                        ) : null}
                    </div>
                )}

                {!isStory && (
                    <div className="recapStats">
                        <div className="recapStat">
                            <div className="recapStatLabel">Mode</div>
                            <div className="recapStatValue">
                                {isBlitz ? "Endless Blitz" : "Endless"}
                            </div>
                        </div>

                        <div className="recapStat">
                            <div className="recapStatLabel">
                                {isBlitz ? "Final Time" : "Run Time"}
                            </div>
                            <div className="recapStatValue">{runElapsedText}</div>
                        </div>

                        <div className="recapStat">
                            <div className="recapStatLabel">
                                Highest Level Reached
                            </div>
                            <div className="recapStatValue">{levelReached}</div>
                        </div>

                        <div className="recapStat">
                            <div className="recapStatLabel">Correct Clicks</div>
                            <div className="recapStatValue">{correctClicks}</div>
                        </div>

                        <div className="recapStat">
                            <div className="recapStatLabel">Treats Earned</div>
                            <div className="recapStatValue">
                                +{Number(treatsEarned).toLocaleString("en-US")}
                            </div>
                        </div>

                        <div className="recapStat">
                            <div className="recapStatLabel">Treats</div>
                            <div className="recapStatValue">
                                {Number(treatBalance).toLocaleString("en-US")}
                            </div>
                        </div>

                        <div className="recapStat">
                            <div className="recapStatLabel">
                                Foils Found (This Run)
                            </div>
                            <div className="recapStatValue">{foilsFound}</div>
                        </div>

                        <div className="recapStat">
                            <div className="recapStatLabel">
                                Hidden Boosts (This Run)
                            </div>
                            <div className="recapStatValue">
                                {hiddenBoostsFound}
                            </div>
                        </div>

                        <div className="recapStat">
                            <div className="recapStatLabel">
                                Hidden Packs (This Run)
                            </div>
                            <div className="recapStatValue">
                                {hiddenPacksFound}
                            </div>
                        </div>

                        <div className="recapStat">
                            <div className="recapStatLabel">Collection</div>
                            <div className="recapStatValue">
                                {totalUnlocked}/{totalCards}
                            </div>
                        </div>
                    </div>
                )}

                {isLoss && (
                    <div className="recapHeartStatus" aria-label="Hearts">
                        <div className="recapHeartStatus__row">
                            <span className="recapHeartStatus__label">Hearts</span>
                            <span
                                className="recapHeartStatus__hearts"
                                aria-label={`${safeHearts} hearts`}
                            >
                                {formatHeartsMeter(safeHearts, maxHearts)}
                            </span>
                        </div>
                        {hardcoreMode ? (
                            <p className="recapHeartStatus__note">
                                Hardcore / Blitz — hearts cannot save a mistake.
                            </p>
                        ) : null}
                        {safeHearts < maxHearts ? (
                            <p className="recapHeartStatus__note">
                                Next heart in {refillCountdown}
                            </p>
                        ) : (
                            <p className="recapHeartStatus__note">
                                {safeHearts > maxHearts
                                    ? `Stash ready · regen full at ${maxHearts}`
                                    : "Hearts full"}
                            </p>
                        )}
                    </div>
                )}

                {showHeartOffer && (
                    <div className="recapContinueOffer">
                        <div className="recapContinueOffer__title">
                            Out of hearts
                        </div>
                        <p className="recapContinueOffer__message">
                            Grab a heart to jump back in while this run is still
                            fresh.
                        </p>

                        {heartRewardError ? (
                            <p className="recapContinueOffer__error" role="alert">
                                {heartRewardError}
                            </p>
                        ) : null}

                        <div className="recapContinueOffer__actions">
                            {showWatchAd ? (
                                <>
                                    <button
                                        type="button"
                                        className="homePrimaryButton"
                                        onClick={onWatchAd}
                                        disabled={isBusy || !adAvailable}
                                    >
                                        {heartRewardBusy === "ad"
                                            ? "Watching…"
                                            : adAvailable
                                              ? "Watch Ad · +1 Heart"
                                              : "Ad limit reached"}
                                    </button>
                                    {adRemaining != null && adAvailable ? (
                                        <p className="recapContinueOffer__hint">
                                            {adRemaining} ad
                                            {adRemaining === 1 ? "" : "s"} left
                                            today
                                            {adStatus?.isPreview
                                                ? " · preview"
                                                : ""}
                                        </p>
                                    ) : null}
                                </>
                            ) : null}

                            <button
                                type="button"
                                className="homePrimaryButton"
                                onClick={onBuyRefill}
                                disabled={isBusy}
                            >
                                {heartRewardBusy === "iap" ||
                                heartRewardBusy === "iap3"
                                    ? "Buying…"
                                    : "+3 Hearts"}
                            </button>
                            <p className="recapContinueOffer__hint">
                                Purchase hearts to jump back in (can go above{" "}
                                {maxHearts})
                                {adStatus?.isPreview !== false
                                    ? " · preview (no charge)"
                                    : ""}
                            </p>
                        </div>
                    </div>
                )}

                <div className="recapButtons">
                    {hasNextStoryStage && (
                        <button
                            className="homePrimaryButton"
                            onClick={onContinueStory}
                        >
                            {enteringUnderworld
                                ? "Enter the Underworld"
                                : `Continue to Level ${nextStoryLevelId}`}
                        </button>
                    )}

                    {clearedUnderworldFinale && (
                        <button
                            className="homePrimaryButton"
                            onClick={onMainMenu}
                        >
                            Return Home
                        </button>
                    )}

                    {isLoss && !outOfHearts && (
                        <button
                            className={`homePrimaryButton${
                                showHeartOffer ? " homeSecondaryButton" : ""
                            }`}
                            onClick={retryHandler}
                            disabled={isBusy}
                        >
                            {retryLabel}
                        </button>
                    )}

                    {isStory && !clearedUnderworldFinale && (
                        <button
                            className={`homePrimaryButton${
                                hasNextStoryStage || isLoss
                                    ? " homeSecondaryButton"
                                    : ""
                            }`}
                            onClick={handlePlayAgain}
                            disabled={isBusy}
                        >
                            Back to Story
                        </button>
                    )}

                    {!isStory && outcome === "win" && (
                        <button
                            className="homePrimaryButton"
                            onClick={handlePlayAgain}
                        >
                            Play Again
                        </button>
                    )}

                    {!clearedUnderworldFinale && (
                        <button
                            className="homePrimaryButton homeSecondaryButton"
                            onClick={onMainMenu}
                            disabled={isBusy}
                        >
                            Main Menu
                        </button>
                    )}
                </div>

                <div className="recapHint">
                    {isStory
                        ? outcome === "win"
                            ? hasNextStoryStage
                                ? clearedMainStoryFinale
                                    ? "Story complete — bonus Underworld stages await."
                                    : "Keep going — the next stage is ready."
                                : clearedUnderworldFinale
                                  ? "Underworld complete. Your Journey frames and titles are unlocked in Cosmetics."
                                  : "Story complete — Endless Mode is fully unlocked."
                            : showHeartOffer
                              ? "Get a heart, then try this stage again."
                              : "Your Story progress is saved. Try this stage again when ready."
                        : outcome === "win"
                          ? "Nice! Your collection progress was saved."
                          : showHeartOffer
                            ? "Get a heart and chase that score again."
                            : "Try again — your collection progress was saved."}
                </div>
            </div>
            <AppFooter
                variant="overlay"
                onPrivacyPolicy={onPrivacyPolicy}
                onTermsOfService={onTermsOfService}
            />
        </div>
    );
};

export default Recap;
